import { Alert, Box, Button, Checkbox, FormControl, FormControlLabel, FormGroup, FormHelperText, FormLabel, Radio, RadioGroup, Typography } from "@mui/material";
import { DatePicker, LocalizationProvider } from "@mui/x-date-pickers";
import { AdapterDateFns } from '@mui/x-date-pickers/AdapterDateFns';
import { compareAsc, parseISO } from "date-fns";
import { useEffect } from "react";
import { useForm, Controller } from "react-hook-form";
import { ConditionalBox } from "../../../../utils/ConditionalBox";
import { DebugBox } from "@northpowerdev/np-react-utils";
import { GrayTextField } from "../../../../utils/GrayTextField";
import { plural } from "../../../../utils/utilityFunctions";
import { connectionTypeDisagrees, fuseRatingDisagrees, phaseListDisagrees, rippleControllerPhaseDisagrees, rippleControllerRecommends, serviceBranchConnectionDisagrees } from "./formConditionFunctions";

export default function IcpInspectionStepDetails({
    formState: {
        meterLocationDescription, connectionDate,
        serviceBranchConnection, connectionType, 
        fuseRating, fuseRatingOther, phaseList, rippleControllerPhase,
    },
    networkApprovedPhaseList,
    networkApprovedParameters,
    dispatch,
    onBack,
    onNext}) {

    const { handleSubmit, control, setValue, formState: { errors, isSubmitted }, trigger } = useForm();

    const onSubmit = (data) => {
        // console.log('IcpInspectionStepDetails onSubmit passed validation with data', data)
        onNext(data)
    }

    useEffect(() => {
        // each time user changes phase selection(s) we clear any selected ripple controller phase
        if (!phaseList.includes(rippleControllerPhase) && rippleControllerPhase !== 'Not Installed') {
            setValue('rippleControllerPhase', '')
            dispatch({type: 'rippleControllerPhase', value: ''})
        }
        if (isSubmitted) {
            trigger() // Make sure we re-validate after phaseList changes
        }
    }, [dispatch, phaseList, rippleControllerPhase, setValue, isSubmitted, trigger])

    const networkApprovedPhaseString = networkApprovedPhaseList?.join(', ')

    const rippleControllerRecommendation = rippleControllerRecommends(networkApprovedParameters?.rippleControllerRequired)

    return (
        <form onSubmit={handleSubmit(onSubmit)}>

            <Typography variant="h4" sx={{mb:3}}>About This Connection</Typography>
            <Box sx={{display: 'flex', flexDirection: 'column' }}>

                <FormControl sx={{my:2}}>
                    <Controller name='meterLocationDescription' control={control}
                        defaultValue={meterLocationDescription}
                        rules={{required: true}}
                        render={({field}) => ( // { onChange, onBlur, value, name, ref }
                            <GrayTextField
                                sx={{my:1}}
                                label="Describe meter board location *"
                                value={meterLocationDescription}
                                error={!!errors.meterLocationDescription}
                                helperText={errors.meterLocationDescription && "Meter Location is required"}
                                onChange={(e) => { field.onChange(e.target.value); dispatch({type: 'meterLocationDescription', value: e.target.value}) } }
                                />
                        )}
                    />
                </FormControl>

                <FormControl sx={{my:2}} error={!!errors.connectionDate}>
                    <Controller name='connectionDate' control={control}
                        defaultValue={connectionDate}
                        rules={{validate: (value) => {
                            const isDate = value instanceof Date && !isNaN(value.valueOf())
                            let before2020 = false
                            if (isDate) {
                                const firstJan2020 = parseISO('2020-01-01T00:00:00+13:00')
                                if (compareAsc(firstJan2020, value) > 0) {
                                    // date is before 1 Jan 2020
                                    before2020 = true
                                }
                            }
                            let afterToday = false
                            if (isDate) {
                                const today = new Date()
                                if (compareAsc(value, today) > 0) {
                                    // date is after today
                                    afterToday = true
                                }
                            }
                            // console.log('connectionDate value', value, (isDate ? 'date' : 'not date'));
                            return (isDate && !before2020 && !afterToday)
                        }}}
                        render={({field}) => ( // { onChange, onBlur, value, name, ref }
                            <LocalizationProvider dateAdapter={AdapterDateFns}>
                            <DatePicker
                                disableFuture
                                label="Connection Date *"
                                value={connectionDate}
                                format="d MMMM yyyy"
                                onChange={(newValue) => { field.onChange(newValue); dispatch({type: 'connectionDate', value: newValue}); }}
                                renderInput={(params) => <GrayTextField {...params} />}
                            />
                            </LocalizationProvider>
                        )}
                    />
                    {errors.connectionDate && <FormHelperText>Connection Date is required and must be before today and after 1 Jan 2020</FormHelperText>}
                </FormControl>

                <FormControl sx={{my:2}} error={!!errors.serviceBranchConnection}>
                    <FormLabel color="secondary">Service Branch Connection * <NetworkApprovalSays recommendation={networkApprovedParameters?.connectionPoint} /></FormLabel>
                    <Controller name='serviceBranchConnection' control={control}
                        defaultValue={serviceBranchConnection}
                        rules={{required: true}}
                        render={({field}) => ( // { onChange, onBlur, value, name, ref }
                            <RadioGroup value={serviceBranchConnection}
                                    onChange={(e) => {field.onChange(e.target.value); dispatch({type: 'serviceBranchConnection', value: e.target.value})}}
                            >
                                <FormControlLabel key="Pole"    value="Pole"                 control={<Radio />} label="Pole" />
                                <FormControlLabel key="Pillar"  value="Pillar"               control={<Radio />} label="Pillar" />
                                <FormControlLabel key="TudPil"  value="Tud Pillar"           control={<Radio />} label="Tud Pillar" />
                                <FormControlLabel key="Transf"  value="Transformer"          control={<Radio />} label="Transformer" />
                                <FormControlLabel key="Master"  value="Master Meter Station" control={<Radio />} label="Master Meter Station" />
                            </RadioGroup>
                        )}
                    />
                    {errors.serviceBranchConnection && <FormHelperText>Service Branch Connection is required</FormHelperText>}
                    <ConditionalBox condition={serviceBranchConnectionDisagrees(serviceBranchConnection, networkApprovedParameters?.connectionPoint)}>
                        <MismatchAlert choice={serviceBranchConnection} recommendation={networkApprovedParameters?.connectionPoint} />
                    </ConditionalBox>
                </FormControl>

                <FormControl sx={{my:2}} error={!!errors.connectionType}>
                    <FormLabel color="secondary">Connection Type * <NetworkApprovalSays recommendation={networkApprovedParameters?.serviceMain} /></FormLabel>
                    <Controller name='connectionType' control={control}
                        defaultValue={connectionType}
                        rules={{required: true}}
                        render={({field}) => ( // { onChange, onBlur, value, name, ref }
                            <RadioGroup value={connectionType}
                                    onChange={(e) => {field.onChange(e.target.value); dispatch({type: 'connectionType', value: e.target.value})}}>
                                <FormControlLabel key="Overhead"    value="Overhead"    control={<Radio />} label="Overhead" />
                                <FormControlLabel key="Underground" value="Underground" control={<Radio />} label="Underground" />
                            </RadioGroup>
                        )}
                    />
                    {errors.connectionType && <FormHelperText>Connection Type is required</FormHelperText>}
                    <ConditionalBox condition={connectionTypeDisagrees(connectionType, networkApprovedParameters?.serviceMain)}>
                        <MismatchAlert choice={connectionType} recommendation={networkApprovedParameters?.serviceMain} />
                    </ConditionalBox>
                </FormControl>

                <FormControl sx={{my:2}} error={!!errors.fuseRating}>
                    <FormLabel color="secondary">Fuse Rating * <NetworkApprovalSays recommendation={networkApprovedParameters?.fuseRating} /></FormLabel>
                    <Controller name='fuseRating' control={control}
                        defaultValue={fuseRating}
                        rules={{required: true}}
                        render={({field}) => ( // { onChange, onBlur, value, name, ref }
                        <RadioGroup value={fuseRating}
                                onChange={(e) => {field.onChange(e.target.value); dispatch({type: 'fuseRating', value: e.target.value})}}>
                            <FormControlLabel key="30 Amp"  value="30 Amp"  control={<Radio />} label="30 Amp" />
                            <FormControlLabel key="60 Amp"  value="60 Amp"  control={<Radio />} label="60 Amp" />
                            <FormControlLabel key="100 Amp" value="100 Amp" control={<Radio />} label="100 Amp" />
                            <FormControlLabel key="Other"   value="Other"   control={<Radio />} label="Other" />
                        </RadioGroup>
                        )}
                    />
                    {errors.fuseRating && <FormHelperText>Fuse Rating is required</FormHelperText>}
                </FormControl>

                <ConditionalBox condition={fuseRating === 'Other'}>
                    <FormControl sx={{mt:-2, mb:2}}>
                        <Controller name='fuseRatingOther' control={control}
                            defaultValue={fuseRatingOther}
                            rules={{required: true}}
                            render={({field}) => ( // { onChange, onBlur, value, name, ref }
                                <GrayTextField
                                    sx={{my:1}}
                                    label="Fuse rating (number of amps) *"
                                    value={fuseRatingOther}
                                    type='number'
                                    error={!!errors.fuseRatingOther}
                                    helperText={errors.fuseRatingOther && "A fuse rating (number of amps) is required"}
                                    onChange={(e) => { field.onChange(e.target.value); dispatch({type: 'fuseRatingOther', value: e.target.value.replace(/[^0-9]/g, '')}) } }
                                    />
                            )}
                        />
                    </FormControl>
                </ConditionalBox>

                <ConditionalBox condition={fuseRatingDisagrees(fuseRating, fuseRatingOther, networkApprovedParameters?.fuseRating)}>
                    <MismatchAlert sx={{mt:-2, mb:3}} choice={fuseRating !== 'Other' ? fuseRating : fuseRatingOther} recommendation={networkApprovedParameters?.fuseRating} />
                </ConditionalBox>

                <FormControl sx={{my:2}} error={isSubmitted && (!phaseList.length || phaseList.length > 3)}>
                    <FormLabel color="secondary">Phase * <NetworkApprovalSays recommendation={networkApprovedPhaseString} /></FormLabel>
                    <FormHelperText></FormHelperText>
                    <Controller name='phaseList' control={control}
                        defaultValue={phaseList}
                        rules={{validate: (value) => { return phaseList.length > 0 && phaseList.length <= 3} }}
                        render={({field}) => ( // { onChange, onBlur, value, name, ref }
                        <FormGroup onChange={(e) => {field.onChange(e.target.value); dispatch({type: 'phaseList', value: {phase: e.target.value, checked: e.target.checked}})}}>
                            <FormControlLabel key="Red"           control={<Checkbox value="Red"           checked={phaseList && phaseList.includes('Red')}    />} label="Red" />
                            <FormControlLabel key="White"         control={<Checkbox value="White"         checked={phaseList && phaseList.includes('White')}  />} label="White" />
                            <FormControlLabel key="Blue"          control={<Checkbox value="Blue"          checked={phaseList && phaseList.includes('Blue')}  />} label="Blue" />
                            <FormControlLabel key="Not Traceable" control={<Checkbox value="Not Traceable" checked={phaseList && phaseList.includes('Not Traceable')}  />} label="Not Traceable" />
                        </FormGroup>
                        )}
                    />
                    {isSubmitted && !phaseList.length && <FormHelperText>Phase is required</FormHelperText>}
                    {isSubmitted && phaseList.length > 3 && <FormHelperText>Three phases or fewer please</FormHelperText>}
                    <ConditionalBox condition={phaseListDisagrees(phaseList, networkApprovedPhaseList)}>
                        <MismatchAlert choice={phaseList.join(', ')} recommendation={networkApprovedPhaseString} />
                    </ConditionalBox>
                </FormControl>

                <FormControl sx={{my:2}} error={!!errors.rippleControllerPhase}>
                    <FormLabel color="secondary">Ripple Controller Phase * <NetworkApprovalSays recommendation={rippleControllerRecommendation} /></FormLabel>
                    <Controller name='rippleControllerPhase' control={control}
                        defaultValue={rippleControllerPhase}
                        rules={{required: true}}
                        render={({field}) => ( // { onChange, onBlur, value, name, ref }
                            <RadioGroup value={rippleControllerPhase}
                                    onChange={(e) => {field.onChange(e.target.value);  dispatch({type: 'rippleControllerPhase', value: e.target.value})}}
                            >
                                {
                                    phaseList.map((phase) => {
                                        return (
                                            <FormControlLabel key={phase} value={phase} control={<Radio />} label={phase} />

                                        )
                                    })
                                }
                                <FormControlLabel key="Not Installed" value="Not Installed" control={<Radio  />} label="Not Installed" />
                            </RadioGroup>
                        )}
                    />
                    {errors.rippleControllerPhase && <FormHelperText>Ripple Controller Phase is required</FormHelperText>}
                    <ConditionalBox condition={rippleControllerPhaseDisagrees(rippleControllerPhase, networkApprovedParameters?.rippleControllerRequired)}>
                        <MismatchAlert choice={rippleControllerPhase} recommendation={rippleControllerRecommendation} />
                    </ConditionalBox>
                </FormControl>

            </Box>
            <Box sx={{ my: 2, display: 'flex', justifyContent: 'space-between' }}>
                <Button onClick={onBack}>Back</Button>
                <Button type="submit" variant="contained">Next</Button>
            </Box>
            {
                Object.keys(errors).length > 0
                &&
                <Box textAlign="right">
                    <Alert sx={{display: 'inline-flex'}} severity="error">We found {Object.keys(errors).length} error{plural(Object.keys(errors).length)}.</Alert>
                </Box>
            }
            <DebugBox title="networkApprovedParameters" stringify>{networkApprovedParameters}</DebugBox>

        </form>
    )
}

function MismatchAlert({choice, recommendation, sx}) {
    return (
        <Alert sx={sx} severity="info">Your selection <strong>{choice}</strong> doesn't match the expected option <strong>{recommendation}</strong>.</Alert> 
    )
}

function NetworkApprovalSays({recommendation}) {
    return (
        (
            recommendation
            ?
                <span>
                    (Network Approval says: <strong>{recommendation}</strong>)
                </span>
            :
            <></>
        )
    )
}